import React from 'react'
import './radar.css'
const CX = 95
const CY = 95
const R_OUTER = 82
const R_INNER = 38
const SW_OUTER = 4
const CENTER_R = 30
function polar(r, deg) {
  const rad = (deg * Math.PI) / 180
  return {
    x: +(CX + r * Math.sin(rad)).toFixed(3),
    y: +(CY - r * Math.cos(rad)).toFixed(3),
  }
}
function buildSegments(legend, totalOverride) {
  const legendTotal = legend.reduce((sum, item) => sum + item.count, 0)
  const total = totalOverride && totalOverride > 0 ? totalOverride : legendTotal
  if (total === 0) return []
  const circumference = 2 * Math.PI * R_OUTER
  let cumulativeDeg = 0
  return legend.map((item) => {
    const fraction = item.count / total
    const arcLength = fraction * circumference
    const gap = circumference - arcLength
    const seg = {
      key: item.key,
      dasharray: `${arcLength} ${gap}`,
      rotateDeg: -90 + cumulativeDeg,
    }
    cumulativeDeg += fraction * 360
    return seg
  })
}
const SEGMENT_COLORS = {
  valid: '#00a65a',
  invalid: '#DC2626',
  catch_all: '#5a90e2',
  unknown: '#9CA3AF',
}
function buildSweepWedges(legend, totalOverride) {
  const legendTotal = legend.reduce((sum, item) => sum + item.count, 0)
  const total = totalOverride && totalOverride > 0 ? totalOverride : legendTotal
  if (total === 0) return []
  let cumulativeDeg = 0
  const wedges = []
  legend.forEach((item) => {
    if (item.count <= 0) return
    const startDeg = cumulativeDeg
    const endDeg = cumulativeDeg + (item.count / total) * 360
    const span = endDeg - startDeg
    const a = polar(R_OUTER, startDeg)
    const b = polar(R_OUTER, endDeg)
    const largeArc = span > 180 ? 1 : 0
    wedges.push({
      key: item.key,
      color: SEGMENT_COLORS[item.key] || '#F97316',
      path: `M ${CX},${CY} L ${a.x},${a.y} A ${R_OUTER},${R_OUTER} 0 ${largeArc} 1 ${b.x},${b.y} Z`,
    })
    cumulativeDeg = endDeg
  })
  return wedges
}
const RadarProgress = ({ model }) => {
  if (!model) return null
  const {
    percent = 0,
    centerPrimary,
    centerSecondary,
    etaLabel,
    legend = [],
    onCancel,
    canShowBreakdown = true,
    totalForRing,
    stageLabel,
    mode,
  } = model
  const sweepDeg = Math.max(0, Math.min(360, (percent / 100) * 360))
  const ringOffset = 2 * Math.PI * R_OUTER * (1 - percent / 100)
  const segments = canShowBreakdown ? buildSegments(legend, totalForRing) : []
  const sweepWedges = canShowBreakdown ? buildSweepWedges(legend, totalForRing) : []
  return (
    <div className='radar-wrap'>
      {(stageLabel || mode) && (
        <div className='radar-header-block'>
          {stageLabel && (
            <h3 className='radar-header-title'>
              {stageLabel} <span className='radar-header-title-suffix'>in progress</span>
            </h3>
          )}
          {mode && (<p className='radar-header-subtitle'>Optimized for <span className='radar-header-mode'>{mode}</span></p>)}
        </div>
      )}
      <div className='radar-svg-holder'>
        <svg
          viewBox='0 0 190 190'
          width='190'
          height='190'
          role='img'
          aria-label={`${percent}% ${centerSecondary}`}
        >
          <circle
            className='radar-track-outer'
            cx={CX}
            cy={CY}
            r={R_OUTER}
            strokeWidth={SW_OUTER}
            fill='none'
          />
          <circle
            className='radar-track-inner'
            cx={CX}
            cy={CY}
            r={R_INNER}
            fill='none'
          />
          {canShowBreakdown ? (
            segments.map((seg) => (
              <circle
                key={seg.key}
                className={`radar-segment radar-segment--${seg.key}`}
                cx={CX}
                cy={CY}
                r={R_OUTER}
                strokeWidth={SW_OUTER}
                fill='none'
                strokeDasharray={seg.dasharray}
                transform={`rotate(${seg.rotateDeg} ${CX} ${CY})`}
              />
            ))
          ) : (
            <circle
              className='radar-progress-ring'
              cx={CX}
              cy={CY}
              r={R_OUTER}
              strokeWidth={SW_OUTER}
              fill='none'
              strokeLinecap='round'
              strokeDasharray={2 * Math.PI * R_OUTER}
              strokeDashoffset={ringOffset}
              transform={`rotate(-90 ${CX} ${CY})`}
            />
          )}
          {sweepWedges.map((w) => (
            <path
              key={w.key}
              d={w.path}
              fill={w.color}
              fillOpacity={0.18}
            />
          ))}
          {sweepDeg > 0 && (
            <line
              className='radar-sweep-line'
              x1={CX}
              y1={CY}
              x2={polar(R_OUTER, sweepDeg).x}
              y2={polar(R_OUTER, sweepDeg).y}
            />
          )}
          <circle
            className='radar-center-bg'
            cx={CX}
            cy={CY}
            r={CENTER_R}
          />
          <text
            className='radar-center-primary'
            x={CX}
            y={CY - 6}
            textAnchor='middle'
            dominantBaseline='middle'
          >
            {centerPrimary != null ? `${centerPrimary}%` : `${percent}%`}
          </text>
          <text
            className='radar-center-secondary-label'
            x={CX}
            y={CY + 8}
            textAnchor='middle'
            dominantBaseline='middle'
          >
            Completed
          </text>
        </svg>
      </div>
      <div className='radar-meta'>
        {etaLabel && (
          <span className='radar-eta'>
            <svg
              className='radar-clock-icon'
              width='14'
              height='14'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle
                cx='12'
                cy='12'
                r='9'
                stroke='currentColor'
                strokeWidth='2'
              />
              <path
                d='M12 7v5l3 3'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </svg>
            {etaLabel} <span className='radar-eta-suffix'>remaining</span>
          </span>
        )}
        {etaLabel && onCancel && <span className='radar-meta-sep'>|</span>}
        {onCancel && (
          <a
            href='#'
            className='radar-cancel'
            onClick={(e) => { e.preventDefault(); onCancel() }}
          >
            Cancel
          </a>
        )}
      </div>
      {legend.length > 0 && (
        <ul className='radar-legend'>
          {legend.map((item) => (
            <li
              key={item.key}
              className='radar-legend-item'
            >
              <span className={`radar-legend-dot radar-legend-dot--${item.key}`} />
              <span className='radar-legend-label'>{item.label}</span>
              <span className='radar-legend-count'>{item.count.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
export default RadarProgress
